import React, { useEffect, useRef, useState } from "react";
import {
  ArrowUpRight,
  Sparkles,
  CheckCircle2,
  Star,
  Lock,
  ExternalLink,
  Volume2,
  VolumeX,
  Check,
  X,
  Zap,
  Crosshair,
  Layers,
  Download,
  FileText,
} from "lucide-react";
import RevealRoot from "@/components/landing/RevealRoot";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SEO from "@/components/SEO";
import { usePrice } from "@/lib/price";

const WHOP = "https://whop.com/checkout/plan_hfsvR7lsOjQds";
import { getMembers } from "@/lib/api";

const GCS = "https://storage.googleapis.com/jobhacki-assets/site/resume-lp/v2";
const GCS_LEGACY = "https://storage.googleapis.com/jobhacki-assets/site/resume-lp";
const GCS_PROD = "https://storage.googleapis.com/jobhacki-assets/product/img";

const VIDEOS = {
  cineScrub: `${GCS}/resume-cine-scrub.mp4`,
  cinePoster: `${GCS}/resume-cine-poster.jpg`,
  clip1: `${GCS}/clip1.mp4`,
  clip1Poster: `${GCS}/clip1-poster.jpg`,
  clip2: `${GCS}/clip2.mp4`,
  clip2Poster: `${GCS}/clip2-poster.jpg`,
};

const IMAGES = {
  builderUi: `${GCS_LEGACY}/builder-ui.png`,
  bentoStyle: `${GCS}/bento-style.jpg`,
  bentoTemplate: `${GCS}/bento-template.jpg`,
  typical: `${GCS_PROD}/typical-template.png`,
  ours: `${GCS_PROD}/our-template.png`,
};

/* ───────── Hero ───────── */
function Hero() {
  const { price } = usePrice();
  const [members, setMembers] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const j = await getMembers();
        const c = j?.count ?? j?.members;
        if (alive && c != null) setMembers(Number(c).toLocaleString());
      } catch {
        /* silent */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section
      data-testid="resume-hero"
      className="relative overflow-hidden border-b-4 border-[#0a0a0a] bg-[#FDFBF7] bg-grid"
    >
      <div className="pointer-events-none absolute -top-24 -right-20 w-[360px] h-[360px] rounded-full bg-[#FFE600] border-4 border-[#0a0a0a] hidden md:block" />
      <div className="pointer-events-none absolute -bottom-24 -left-16 w-[240px] h-[240px] bg-[#FF3B30] border-4 border-[#0a0a0a] rotate-12 hidden md:block" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-20 md:pb-24">
        <div className="reveal max-w-4xl">
          <div className="inline-flex items-center gap-2 bg-[#0a0a0a] text-[#FFE600] border-2 border-[#0a0a0a] px-3 py-1.5 font-accent text-xs md:text-sm shadow-brutal-sm">
            <span className="live-dot" /> AI RESUME BUILDER
          </div>
          <h1
            data-testid="resume-hero-title"
            aria-label="Beat the bots. Land the interview."
            className="font-display mt-7 text-[44px] sm:text-6xl md:text-7xl lg:text-[6rem] leading-[0.9]"
          >
            Beat the bots.
            <br />
            Land the <span className="bg-[#FFE600] px-2">interview</span>.
          </h1>
          <p className="mt-6 max-w-2xl font-mono-body text-base md:text-lg text-[#0a0a0a]/80 leading-relaxed">
            Paste your history, target the role, and get an ATS-ready resume
            back in minutes — bullets rewritten, scored against the posting,
            exported clean.
          </p>
          <div className="mt-6 flex items-center gap-3 font-mono-body text-sm">
            <span className="live-dot" />
            <b className="font-display tracking-wide">{members || "1,700+"} members</b>
            <span className="text-[#0a0a0a]/65">building right now</span>
          </div>
          <div className="mt-8 flex flex-col sm:flex-row gap-3.5">
            <a
              href={WHOP}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="resume-hero-cta-primary"
              className="hover-pop inline-flex items-center justify-center gap-2 bg-[#FF3B30] text-white border-4 border-[#0a0a0a] px-6 py-4 font-accent text-base md:text-lg shadow-brutal"
            >
              <Lock size={18} strokeWidth={3} /> GET ALL-ACCESS — {price}
              <ExternalLink size={14} strokeWidth={3} />
            </a>
            <a
              href="/login"
              data-testid="resume-hero-cta-secondary"
              className="hover-pop inline-flex items-center justify-center gap-2 bg-[#FFE600] text-[#0a0a0a] border-4 border-[#0a0a0a] px-6 py-4 font-accent text-base md:text-lg shadow-brutal"
            >
              Build my resume — free
              <ArrowUpRight size={18} strokeWidth={3} />
            </a>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono-body text-xs md:text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-[#0055FF]" />
              One-time {price} · lifetime
            </div>
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-[#FF3B30]" />7-day Whop refund
            </div>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} size={12} fill="#FFE600" strokeWidth={1.5} className="text-[#0a0a0a]" />
              ))}
              <span className="font-bold">4.9 on Whop</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───────── Scrub video — ATS scan ───────── */
function ScrubVideo() {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);

  return (
    <section
      data-testid="resume-scrub"
      className="border-b-4 border-[#0a0a0a] bg-[#0a0a0a] text-[#FDFBF7]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="text-center mb-8 reveal">
          <div className="font-accent text-sm bg-[#FFE600] text-[#0a0a0a] inline-block px-3 py-1 mb-4">
            ATS SCAN — LIVE
          </div>
          <h2 className="font-display text-3xl md:text-5xl leading-[0.95]">
            Your résumé,{" "}
            <span className="text-[#FFE600]">scanned and rebuilt</span>
            <br />
            to clear ATS.
          </h2>
        </div>
        <div className="relative border-4 border-[#FFE600] shadow-brutal-lg bg-[#0a0a0a] reveal">
          <video
            ref={ref}
            src={VIDEOS.cineScrub}
            poster={VIDEOS.cinePoster}
            muted={muted}
            playsInline
            autoPlay
            loop
            preload="metadata"
            className="w-full h-auto block"
            data-testid="resume-scrub-video"
          />
          <span className="absolute top-4 left-4 inline-flex items-center gap-2 bg-[#0a0a0a] text-[#FFE600] border-2 border-[#FFE600] px-3 py-1.5 font-accent text-xs shadow-brutal-sm">
            <span className="live-dot" /> ATS SCAN
          </span>
          <button
            data-testid="resume-scrub-mute"
            onClick={() => {
              setMuted((m) => !m);
              if (ref.current) ref.current.muted = !muted ? true : false;
            }}
            className="absolute top-4 right-4 hover-pop inline-flex items-center gap-2 bg-[#FFE600] text-[#0a0a0a] border-2 border-[#0a0a0a] px-3 py-2 font-accent text-xs shadow-brutal-sm"
          >
            {muted ? <VolumeX size={14} strokeWidth={3} /> : <Volume2 size={14} strokeWidth={3} />}
            {muted ? "Sound" : "Mute"}
          </button>
        </div>
      </div>
    </section>
  );
}

/* ───────── Proof bar ───────── */
function ProofRow() {
  const { price } = usePrice();
  return (
    <section
      data-testid="resume-proof-row"
      className="border-b-4 border-[#0a0a0a] bg-[#FFE600]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-6 flex flex-wrap items-center justify-center gap-4 md:gap-8 font-accent text-xs md:text-sm">
        <div className="flex items-center gap-2">
          <span className="live-dot" /> <b className="font-display text-base md:text-lg">1,700+</b> members
        </div>
        <span className="hidden sm:inline opacity-30">·</span>
        <a
          href="https://whop.com/jobhacki/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 hover:underline underline-offset-4 decoration-2"
        >
          <img
            src="https://icon.horse/icon/whop.com"
            alt="Whop"
            className="w-4 h-4"
            loading="lazy"
          />
          Whop
          <span className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} size={11} fill="#0a0a0a" strokeWidth={0} />
            ))}
          </span>
          <span className="opacity-70">4.9 · 10 reviews</span>
        </a>
        <span className="hidden sm:inline opacity-30">·</span>
        <span className="border-2 border-[#0a0a0a] bg-white px-2.5 py-1">{price} one-time · no subscription</span>
      </div>
    </section>
  );
}

/* ───────── Generic clip frame ───────── */
function ClipFrame({ src, poster, tag, score, label, captionLabel, testid, accent = "#FFE600" }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  return (
    <div className="reveal">
      <figure className="relative border-4 border-[#0a0a0a] bg-[#0a0a0a] shadow-brutal-lg overflow-hidden">
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          muted={muted}
          playsInline
          autoPlay
          loop
          preload="metadata"
          className="w-full h-auto block"
          data-testid={testid}
        />
        {tag && (
          <span
            className="absolute top-4 left-4 inline-flex items-center gap-2 bg-[#0a0a0a] text-[#FFE600] border-2 px-3 py-1.5 font-accent text-xs shadow-brutal-sm"
            style={{ borderColor: accent }}
          >
            <span className="live-dot" /> {tag}
          </span>
        )}
        {score && (
          <span className="absolute top-4 right-16 bg-[#00FF66] text-[#0a0a0a] border-2 border-[#0a0a0a] px-3 py-1.5 font-accent text-xs shadow-brutal-sm flex items-baseline gap-1.5">
            <b className="font-display text-base">{score}</b>
            <small className="text-[10px]">{label}</small>
          </span>
        )}
        <button
          onClick={() => {
            setMuted((m) => !m);
            if (videoRef.current) videoRef.current.muted = !muted ? true : false;
          }}
          className="absolute top-4 right-4 hover-pop bg-[#FFE600] text-[#0a0a0a] border-2 border-[#0a0a0a] w-9 h-9 inline-flex items-center justify-center shadow-brutal-sm"
          data-testid={`${testid}-mute`}
          aria-label="Toggle sound"
        >
          {muted ? <VolumeX size={14} strokeWidth={3} /> : <Volume2 size={14} strokeWidth={3} />}
        </button>
      </figure>
      {captionLabel && (
        <div className="text-center font-accent text-xs mt-3 text-[#0a0a0a]/70">
          {captionLabel}
        </div>
      )}
    </div>
  );
}

/* ───────── See it build — Clip 1 ───────── */
function SeeItBuild() {
  return (
    <section
      data-testid="resume-builder-clip"
      className="border-b-4 border-[#0a0a0a] bg-[#FDFBF7]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="text-center mb-10 reveal">
          <div className="font-accent text-sm bg-[#0055FF] text-white inline-block px-3 py-1 mb-4">
            SEE IT BUILD
          </div>
          <h2 className="font-display text-[40px] sm:text-5xl md:text-6xl leading-[0.95] max-w-3xl mx-auto">
            Blank to{" "}
            <span className="bg-[#FFE600] px-2">recruiter-ready</span>, on one screen.
          </h2>
          <p className="font-mono-body text-base md:text-lg mt-6 max-w-2xl mx-auto text-[#0a0a0a]/75">
            Edit on the left, watch your resume reflow live on the right — no
            fighting margins, tables, or columns that break parsers.
          </p>
        </div>
        <ClipFrame
          src={VIDEOS.clip1}
          poster={VIDEOS.clip1Poster}
          tag="LIVE PREVIEW"
          score="94"
          label="ATS SCORE"
          captionLabel="CLIP 01 — THE BUILDER · 0:58"
          testid="clip-builder-video"
        />
      </div>
    </section>
  );
}

/* ───────── The score — Clip 2 ───────── */
function TheScore() {
  return (
    <section
      data-testid="resume-score"
      className="border-b-4 border-[#0a0a0a] bg-[#0055FF] text-white"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="text-center mb-10 reveal">
          <div className="font-accent text-sm bg-[#FFE600] text-[#0a0a0a] inline-block px-3 py-1 mb-4">
            THE SCORE
          </div>
          <h2 className="font-display text-[40px] sm:text-5xl md:text-6xl leading-[0.95] max-w-3xl mx-auto">
            Know your odds{" "}
            <span className="bg-[#FFE600] text-[#0a0a0a] px-2">before</span> you apply.
          </h2>
          <p className="font-mono-body text-base md:text-lg mt-6 max-w-2xl mx-auto text-white/85">
            Most resumes get filtered by software before a human ever reads
            them. We test yours against the exact posting, then hand you the
            fix-list to clear the bar.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-stretch mb-8">
          <div className="lg:col-span-5 bg-[#0a0a0a] border-4 border-[#FFE600] shadow-brutal-lg p-7 reveal">
            <div className="font-accent text-xs text-[#FFE600] mb-3">
              ATS MATCH
            </div>
            <div className="flex items-baseline gap-2 mb-5">
              <span className="font-display text-6xl md:text-7xl text-[#FFE600]">30</span>
              <span className="font-mono-body text-lg text-white/60">/ 100</span>
            </div>
            <div className="h-3 bg-[#FDFBF7]/10 border-2 border-[#FFE600] mb-6">
              <div className="h-full bg-[#FFE600] w-[30%]" />
            </div>
            <ul className="space-y-3 font-mono-body text-sm">
              <li className="flex items-start gap-2">
                <Crosshair size={16} strokeWidth={2.5} className="mt-0.5 shrink-0 text-[#FFE600]" />
                Keyword &amp; skills match to the posting
              </li>
              <li className="flex items-start gap-2">
                <Zap size={16} strokeWidth={2.5} className="mt-0.5 shrink-0 text-[#FFE600]" />
                Quantified, recruiter-ready bullet rewrites
              </li>
              <li className="flex items-start gap-2">
                <Check size={16} strokeWidth={3} className="mt-0.5 shrink-0 text-[#00FF66]" />
                A ranked fix-list to climb the score
              </li>
            </ul>
          </div>
          <div className="lg:col-span-7">
            <ClipFrame
              src={VIDEOS.clip2}
              poster={VIDEOS.clip2Poster}
              tag="ATS SCORING"
              captionLabel="CLIP 02 — ATS SCORING · 0:23"
              testid="clip-ats-video"
              accent="#FFE600"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───────── Bento — What it actually does ───────── */
function Bento() {
  const tiles = [
    {
      span: "md:col-span-7 md:row-span-2",
      bg: "bg-[#FFE600]",
      mark: { Icon: Layers, label: "The builder" },
      title: "Edit on the left, watch it rebuild on the right.",
      body: "Type your details and the resume restyles live — no fighting with margins, tables, or columns that break ATS parsers.",
      img: IMAGES.builderUi,
    },
    {
      span: "md:col-span-5",
      bg: "bg-white",
      mark: { Icon: Sparkles, label: "Restyle live" },
      title: "Recruiter-tested templates.",
      body: "Classic, Modern, Elegant, Bold — swap fonts, colors and layout in one click.",
      img: IMAGES.bentoStyle,
    },
    {
      span: "md:col-span-5",
      bg: "bg-[#FF3B30] text-white",
      mark: { Icon: Crosshair, label: "Target the role" },
      title: "Tailored to the posting.",
      body: "Point it at any job; it re-weights your resume to that role, not a generic one.",
      img: IMAGES.bentoTemplate,
    },
    {
      span: "md:col-span-6",
      bg: "bg-[#0055FF] text-white",
      mark: { Icon: Download, label: "Export" },
      title: "Clean Word & PDF.",
      body: "Download a pixel-clean file the minute you're done — ready to send.",
    },
    {
      span: "md:col-span-6",
      bg: "bg-[#00FF66]",
      mark: { Icon: Zap, label: "Bullets" },
      title: "\u201CResponsible for\u201D → outcomes.",
      body: "AI rewrites weak lines into quantified, outcome-first bullets recruiters scan for.",
    },
  ];

  return (
    <section
      data-testid="resume-bento"
      className="border-b-4 border-[#0a0a0a] bg-[#FDFBF7]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="mb-10 md:mb-14 reveal relative">
          <span className="absolute -top-6 left-0 font-display text-[140px] leading-none text-[#0a0a0a]/[0.06] select-none pointer-events-none">
            02
          </span>
          <div className="font-accent text-sm bg-[#0a0a0a] text-[#FFE600] inline-block px-3 py-1 mb-4 relative">
            WHAT IT ACTUALLY DOES
          </div>
          <h2 className="font-display text-[40px] sm:text-5xl md:text-6xl leading-[0.95] relative">
            Real rewrites. Real exports.{" "}
            <span className="bg-[#FFE600] px-2">No filler.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 md:auto-rows-fr gap-5 md:gap-6">
          {tiles.map((t, i) => {
            const Icon = t.mark.Icon;
            return (
              <article
                key={i}
                data-testid={`resume-bento-tile-${i}`}
                className={`${t.span || ""} ${t.bg} border-4 border-[#0a0a0a] shadow-brutal hover-pop p-6 md:p-8 flex flex-col reveal`}
              >
                {t.img && (
                  <div className="border-4 border-[#0a0a0a] mb-5 overflow-hidden">
                    <img src={t.img} alt={t.mark.label} loading="lazy" className="w-full h-auto block" />
                  </div>
                )}
                <span className="inline-flex items-center gap-1.5 self-start font-accent text-[10px] md:text-xs bg-[#0a0a0a] text-[#FFE600] border-2 border-[#0a0a0a] px-2 py-1 mb-3">
                  <Icon size={12} strokeWidth={3} /> {t.mark.label}
                </span>
                <h3 className="font-display text-2xl md:text-3xl leading-tight">
                  {t.title}
                </h3>
                <p className="font-mono-body text-sm md:text-base mt-2.5 opacity-90 leading-relaxed">
                  {t.body}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ───────── Promise + proof wall ───────── */
function PromiseWall() {
  return (
    <section
      data-testid="resume-wall"
      className="border-b-4 border-[#0a0a0a] bg-[#FFE600]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <blockquote className="font-display text-4xl sm:text-5xl md:text-6xl leading-[0.95] max-w-4xl reveal">
          Most resumes never reach a human.{" "}
          <em className="not-italic bg-[#0a0a0a] text-[#FFE600] px-2">Yours will.</em>
        </blockquote>
        <div className="font-accent text-sm mt-3 text-[#0a0a0a]/70 reveal">
          — THE JOBHACKI PROMISE
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mt-12">
          <div className="bg-white border-4 border-[#0a0a0a] shadow-brutal p-6 reveal">
            <div className="flex items-center gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} size={16} fill="#FFE600" strokeWidth={1.5} className="text-[#0a0a0a]" />
              ))}
            </div>
            <div className="font-display text-6xl leading-none">4.9</div>
            <div className="font-accent text-sm mt-2">ON WHOP · 10 REVIEWS</div>
            <p className="font-mono-body text-sm mt-3 text-[#0a0a0a]/75">
              Verified rating from the JobHacki membership.{" "}
              <a
                href="https://whop.com/jobhacki/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold border-b-2 border-[#0a0a0a]"
              >
                Read them →
              </a>
            </p>
          </div>
          <div className="bg-[#0a0a0a] text-[#FFE600] border-4 border-[#0a0a0a] shadow-brutal p-6 reveal">
            <div className="font-display text-6xl leading-none">1,700+</div>
            <div className="font-accent text-sm mt-2">MEMBERS</div>
            <p className="font-mono-body text-sm mt-3 text-[#FDFBF7]/75">
              Building resumes, working playbooks and landing interviews inside the community.
            </p>
          </div>
          <div className="bg-[#FF3B30] text-white border-4 border-[#0a0a0a] shadow-brutal p-6 reveal">
            <div className="font-accent text-sm mb-4">BUILT FOR OUTCOMES</div>
            <div className="flex flex-wrap gap-2 mb-4">
              {["ATS-scored", "Word & PDF", "40+ industries", "No subscription"].map((c) => (
                <span
                  key={c}
                  className="bg-white text-[#0a0a0a] border-2 border-[#0a0a0a] px-2.5 py-1 font-accent text-[11px]"
                >
                  {c}
                </span>
              ))}
            </div>
            <p className="font-mono-body text-sm text-white/85">
              Real features, real exports — every claim on this page is countable.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───────── Rebuild side-by-side ───────── */
function RebuildProof() {
  return (
    <section
      data-testid="resume-rebuild"
      className="border-b-4 border-[#0a0a0a] bg-[#0a0a0a] text-[#FDFBF7]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="mb-10 reveal max-w-3xl">
          <div className="font-accent text-sm bg-[#FFE600] text-[#0a0a0a] inline-block px-3 py-1 mb-4">
            THE ACTUAL REBUILD
          </div>
          <h2 className="font-display text-[40px] sm:text-5xl md:text-6xl leading-[0.95]">
            Other tools <span className="bg-[#FF3B30] px-2">talk</span> about your resume.
            <br />
            JobHacki <span className="bg-[#FFE600] text-[#0a0a0a] px-2">rebuilds</span> it.
          </h2>
          <p className="font-mono-body mt-5 text-[#FDFBF7]/75">
            Most &ldquo;AI resume tools&rdquo; hand you advice and scores you still apply yourself. JobHacki does the work: your history goes in, the rebuilt resume comes out — then take it straight to the{" "}
            <a href="/jobs" className="font-bold border-b-2 border-[#FFE600] text-[#FFE600]">
              Job Directory
            </a>{" "}
            and apply.
          </p>
        </div>

        <p className="font-mono-body text-sm md:text-base mb-6 reveal">
          <b className="font-accent text-[#FFE600]">Theirs: text advice. Ours: the rebuilt file.</b> Same work history, side by side:
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <figure className="bg-white text-[#0a0a0a] border-4 border-[#FDFBF7] shadow-brutal-lg p-4 md:p-5 reveal">
            <span className="inline-block bg-[#FF3B30] text-white border-2 border-[#0a0a0a] px-2.5 py-1 font-accent text-xs mb-3">
              TYPICAL TEMPLATE
            </span>
            <div className="border-2 border-[#0a0a0a]/20 overflow-hidden">
              <img
                src={IMAGES.typical}
                alt="Typical generic resume template"
                loading="lazy"
                className="w-full h-auto block"
              />
            </div>
            <figcaption className="font-mono-body text-xs mt-3 text-[#0a0a0a]/70 leading-relaxed">
              A generic builder-style resume (this mock was written and styled by us — not a screenshot of any competitor): vague objective, duty-list bullets, no role targeting.
            </figcaption>
          </figure>
          <figure className="bg-[#FFE600] text-[#0a0a0a] border-4 border-[#FFE600] shadow-brutal-lg p-4 md:p-5 reveal">
            <span className="inline-block bg-[#0a0a0a] text-[#FFE600] border-2 border-[#0a0a0a] px-2.5 py-1 font-accent text-xs mb-3">
              JOBHACKI TEMPLATE
            </span>
            <div className="border-2 border-[#0a0a0a]/30 overflow-hidden">
              <img
                src={IMAGES.ours}
                alt="JobHacki rebuilt resume output"
                loading="lazy"
                className="w-full h-auto block"
              />
            </div>
            <figcaption className="font-mono-body text-xs mt-3 leading-relaxed">
              Real output from the live builder: the same messy work-history paste, rebuilt into a print-ready, role-targeted one-pager.
            </figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
}

/* ───────── Comparison checklist ───────── */
const COMPETITORS = [
  { name: "Teal+", price: "$29/mo" },
  { name: "Rezi", price: "$29/mo" },
  { name: "Kickresume", price: "$24/mo" },
  { name: "Zety", price: "$25.95/4wks" },
  { name: "Resume.io", price: "$29.95/4wks" },
  { name: "Novoresume", price: "$21.99/mo" },
  { name: "Enhancv", price: "$24.99/mo" },
  { name: "LiveCareer", price: "$23.95/4wks" },
  { name: "MyPerfectResume", price: "$23.95/4wks" },
  { name: "Resume Genius", price: "$23.95/4wks" },
  { name: "Canva", price: "Free–$15/mo" },
  { name: "FlowCV", price: "Free–$5/mo" },
];

// 12-row x 12-competitor matrix (1 = yes, 0 = no). Mirrors jobhacki.com/resume.
const FEATURES = [
  { feature: "Actually rebuilds your uploaded resume", us: 1, them: [0, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 0] },
  { feature: "ATS-friendly resume builder", us: 1, them: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 1] },
  { feature: "Targets your resume to the specific job posting", us: 1, them: [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0] },
  { feature: "Job tracker + Chrome extension", us: 0, them: [1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
  { feature: "Designer template gallery / personal-site builder", us: 0, them: [0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1] },
  { feature: "Cover letter builder", us: 0, them: [0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1] },
  { feature: "Interview testing included — same price", us: 1, them: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
  { feature: "100+ income playbooks included", us: 1, them: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
  { feature: "Live job directory included (338K+ listings)", us: 1, them: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
  { feature: "Usable free tier", us: 1, them: [1, 1, 0, 0, 0, 1, 0, 0, 0, 0, 1, 1] },
  { feature: "One-time lifetime purchase option", us: 0, them: [0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
  { feature: "No weekly / 4-week billing cycles", us: 1, them: [0, 1, 1, 0, 0, 1, 1, 0, 0, 0, 1, 1] },
];

function Comparison() {
  const { price } = usePrice();
  const [active, setActive] = useState(0);

  return (
    <section
      id="compare"
      data-testid="resume-compare"
      className="border-b-4 border-[#0a0a0a] bg-[#FDFBF7]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="mb-10 reveal max-w-3xl">
          <div className="font-accent text-sm bg-[#0a0a0a] text-[#FFE600] inline-block px-3 py-1 mb-4">
            HOW IT COMPARES
          </div>
          <h2 className="font-display text-[40px] sm:text-5xl md:text-6xl leading-[0.95]">
            Resume Builder vs the tools{" "}
            <span className="bg-[#FFE600] px-2">people actually pay for.</span>
          </h2>
          <p className="font-mono-body mt-5 text-[#0a0a0a]/75">
            Same checklist, straight answers — where a competitor is stronger, the check is theirs and the ✗ is ours. Prices verified June 2026.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-6 reveal">
          {COMPETITORS.map((c, i) => (
            <button
              key={c.name}
              onClick={() => setActive(i)}
              data-testid={`compare-tab-${i}`}
              className={`font-accent text-xs md:text-sm border-2 border-[#0a0a0a] px-3 py-1.5 transition-colors ${
                i === active
                  ? "bg-[#0a0a0a] text-[#FFE600] shadow-brutal-sm"
                  : "bg-white text-[#0a0a0a] hover:bg-[#FFE600]"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        <div className="border-4 border-[#0a0a0a] shadow-brutal-lg bg-white overflow-hidden reveal">
          <div className="grid grid-cols-[1fr_120px_120px] md:grid-cols-[2fr_140px_140px] bg-[#0a0a0a] text-[#FFE600] font-accent text-[11px] md:text-sm">
            <div className="px-4 py-3 border-r-2 border-[#FFE600]">FEATURE</div>
            <div className="px-4 py-3 border-r-2 border-[#FFE600] text-center">
              JOBHACKI
              <div className="font-mono-body text-[10px] text-[#FFE600]/70 normal-case">All-Access</div>
            </div>
            <div className="px-4 py-3 text-center">
              {COMPETITORS[active].name}
              <div className="font-mono-body text-[10px] text-[#FFE600]/70 normal-case">
                {COMPETITORS[active].price}
              </div>
            </div>
          </div>

          {FEATURES.map((f, i) => {
            const themActive = f.them[active];
            return (
              <div
                key={i}
                className={`grid grid-cols-[1fr_120px_120px] md:grid-cols-[2fr_140px_140px] border-t-2 border-[#0a0a0a]/10 ${
                  i % 2 ? "bg-[#FDFBF7]" : "bg-white"
                }`}
              >
                <div className="px-4 py-3 font-mono-body text-[13px] md:text-sm flex items-center">
                  {f.feature}
                </div>
                <div className="px-4 py-3 border-l-2 border-[#0a0a0a]/10 flex items-center justify-center">
                  {f.us ? (
                    <Check size={20} strokeWidth={4} className="text-[#0055FF]" />
                  ) : (
                    <X size={20} strokeWidth={4} className="text-[#FF3B30]" />
                  )}
                </div>
                <div className="px-4 py-3 border-l-2 border-[#0a0a0a]/10 flex items-center justify-center">
                  {themActive ? (
                    <Check size={20} strokeWidth={4} className="text-[#0a0a0a]" />
                  ) : (
                    <X size={20} strokeWidth={4} className="text-[#FF3B30]" />
                  )}
                </div>
              </div>
            );
          })}

          <div className="grid grid-cols-[1fr_120px_120px] md:grid-cols-[2fr_140px_140px] bg-[#FFE600] border-t-2 border-[#0a0a0a]">
            <div className="px-4 py-3 font-accent text-sm md:text-base">PRICE</div>
            <div className="px-4 py-3 border-l-2 border-[#0a0a0a] flex items-center justify-center font-display text-2xl md:text-3xl">
              {price}
            </div>
            <div className="px-4 py-3 border-l-2 border-[#0a0a0a] flex items-center justify-center font-display text-base md:text-lg">
              {COMPETITORS[active].price}
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          <div className="md:col-span-7 reveal">
            <p className="font-mono-body text-sm md:text-base text-[#0a0a0a]/85">
              Stacking the closest standalone for each JobHacki tool runs{" "}
              <b className="line-through text-[#FF3B30]">~$173/mo</b> (~$2,076/yr at June 2026 prices).
            </p>
            <p className="font-display text-2xl md:text-3xl mt-1">
              All-Access: <span className="bg-[#FFE600] px-2">{price}</span> — all five tools, one membership.
            </p>
          </div>
          <div className="md:col-span-5 flex md:justify-end reveal">
            <a
              href={WHOP}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="compare-cta"
              className="hover-pop inline-flex items-center justify-center gap-2 bg-[#FF3B30] text-white border-4 border-[#0a0a0a] px-6 py-4 font-accent text-base shadow-brutal"
            >
              <Lock size={16} strokeWidth={3} /> Get All-Access — {price}
              <ExternalLink size={14} strokeWidth={3} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───────── Pricing ───────── */
function Pricing() {
  const { price } = usePrice();
  return (
    <section
      id="pricing"
      data-testid="resume-pricing"
      className="border-b-4 border-[#0a0a0a] bg-[#0a0a0a] text-[#FDFBF7]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="text-center mb-12 reveal">
          <div className="font-accent text-sm bg-[#FFE600] text-[#0a0a0a] inline-block px-3 py-1 mb-4">
            PRICING
          </div>
          <h2 className="font-display text-[40px] sm:text-5xl md:text-6xl leading-[0.95]">
            Build free. Export for{" "}
            <span className="bg-[#FFE600] text-[#0a0a0a] px-2">{price}</span>. That&apos;s it.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
          <article className="bg-[#FDFBF7] text-[#0a0a0a] border-4 border-[#FDFBF7] shadow-brutal-lg p-7 md:p-8 reveal flex flex-col">
            <span className="font-accent text-xs bg-[#0a0a0a] text-[#FFE600] inline-block px-2.5 py-1 self-start">
              BUILD
            </span>
            <div className="font-display text-6xl md:text-7xl mt-4">Free</div>
            <p className="font-mono-body text-sm mt-2 text-[#0a0a0a]/70">
              No card. Build and score in the browser.
            </p>
            <ul className="space-y-2.5 mt-6 font-mono-body text-sm flex-1">
              {["Full resume builder & live preview", "ATS score against any posting", "Every template & restyle"].map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <Check size={16} strokeWidth={3} className="mt-0.5 shrink-0 text-[#0055FF]" /> {b}
                </li>
              ))}
            </ul>
            <a
              href="/login"
              data-testid="pricing-free-cta"
              className="hover-pop mt-6 inline-flex items-center justify-center gap-2 bg-[#FDFBF7] text-[#0a0a0a] border-4 border-[#0a0a0a] px-5 py-3.5 font-accent text-base shadow-brutal"
            >
              Start building <ArrowUpRight size={16} strokeWidth={3} />
            </a>
          </article>

          <article className="relative bg-[#FF3B30] text-white border-4 border-[#FFE600] shadow-[8px_8px_0_0_#FFE600] p-7 md:p-8 reveal flex flex-col">
            <span className="absolute -top-5 -right-3 bg-[#00FF66] text-[#0a0a0a] border-4 border-[#0a0a0a] px-3 py-1 font-accent text-xs shadow-brutal rotate-[6deg]">
              MOST POPULAR
            </span>
            <span className="font-accent text-xs bg-[#FFE600] text-[#0a0a0a] inline-block px-2.5 py-1 self-start">
              ALL-ACCESS EXPORT
            </span>
            <div className="flex items-baseline gap-2 mt-4">
              <span className="font-display text-6xl md:text-7xl">{price}</span>
              <span className="font-mono-body text-base text-white/80">one-time</span>
            </div>
            <p className="font-mono-body text-sm mt-2 text-white/85">
              No subscription. Unlocks every tool + the community.
            </p>
            <ul className="space-y-2.5 mt-6 font-mono-body text-sm flex-1">
              {[
                "Unlimited Word & PDF export",
                "AI bullet rewriting & tailoring",
                "Every JobHacki tool + 1,700+ member community",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <Check size={16} strokeWidth={3} className="mt-0.5 shrink-0 text-[#FFE600]" /> {b}
                </li>
              ))}
            </ul>
            <a
              href={WHOP}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pricing-paid-cta"
              className="hover-pop mt-6 inline-flex items-center justify-center gap-2 bg-[#FFE600] text-[#0a0a0a] border-4 border-[#0a0a0a] px-5 py-3.5 font-accent text-base shadow-brutal"
            >
              <Lock size={16} strokeWidth={3} /> Get All-Access — {price}
              <ExternalLink size={14} strokeWidth={3} />
            </a>
          </article>
        </div>
      </div>
    </section>
  );
}

/* ───────── Final CTA ───────── */
function FinalCTA() {
  const { price } = usePrice();
  return (
    <section
      data-testid="resume-final"
      className="relative border-b-4 border-[#0a0a0a] bg-[#FFE600] overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid opacity-25 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28 text-center">
        <div className="inline-flex items-center gap-2 bg-[#0a0a0a] text-[#FFE600] border-2 border-[#0a0a0a] px-3 py-1.5 font-accent text-xs md:text-sm shadow-brutal-sm mb-7">
          <FileText size={14} strokeWidth={3} /> YOUR NEXT INTERVIEW STARTS HERE
        </div>
        <h2 className="font-display text-[44px] sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.92] max-w-4xl mx-auto">
          Your next interview{" "}
          <span className="bg-[#0a0a0a] text-[#FFE600] px-3">starts with the resume.</span>
        </h2>
        <p className="font-mono-body text-base md:text-lg mt-6 text-[#0a0a0a]/80">
          Free to build · {price} to export
        </p>
        <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href={WHOP}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="resume-final-paid"
            className="hover-pop inline-flex items-center justify-center gap-2 bg-[#FF3B30] text-white border-4 border-[#0a0a0a] px-7 py-5 font-accent text-lg md:text-xl shadow-brutal"
          >
            <Lock size={18} strokeWidth={3} /> Get All-Access — {price}
            <ExternalLink size={16} strokeWidth={3} />
          </a>
          <a
            href="/login"
            data-testid="resume-final-free"
            className="hover-pop inline-flex items-center justify-center gap-2 bg-[#0a0a0a] text-[#FFE600] border-4 border-[#0a0a0a] px-7 py-5 font-accent text-lg md:text-xl shadow-brutal"
          >
            Build my resume — free
            <ArrowUpRight size={18} strokeWidth={3} />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ───────── Page ───────── */
export default function Resume() {
  const { price } = usePrice();
  return (
    <div
      data-testid="resume-page"
      className="bg-[#FDFBF7] text-[#0a0a0a] min-h-screen"
    >
      <RevealRoot>
        <SEO
          title="JobHacki Resume Builder — beat the bots, land the interview"
          description={`Paste your history, target a role, get an ATS-ready resume back in minutes — AI bullet rewriting, ATS scoring, clean Word & PDF export. Build free; All-Access export is a ${price} one-time.`}
          canonical="https://jobhacki.com/resume"
          image="https://storage.googleapis.com/jobhacki-assets/site/resume-lp/v2/resume-cine-poster.jpg"
        />
        <Navbar />
        <Hero />
        <ScrubVideo />
        <ProofRow />
        <SeeItBuild />
        <TheScore />
        <Bento />
        <PromiseWall />
        <RebuildProof />
        <Comparison />
        <Pricing />
        <FinalCTA />
        <Footer />
      </RevealRoot>
    </div>
  );
}
